import { Card, CardBody, CardHeader, CardTitle, StackLayout } from "@progress/kendo-react-layout";

const summaryTiles = [
    { label: "Insights Generated", value: "28", delta: "+6 vs last week" },
    { label: "Actionable", value: "19", delta: "68% adoption potential" },
    { label: "At-Risk Scenarios", value: "4", delta: "Needs intervention" },
    { label: "Avg. Confidence", value: "91%", delta: "High certainty" }
] as const;

const trendSignals = [
    {
        title: "Price Elasticity Shift",
        detail: "Elasticity increased 9% in metro stores when discounts exceed 15%.",
        impact: "Revenue lift potential +$180K"
    },
    {
        title: "Promotion Fatigue",
        detail: "Back-to-back promotions reduced incrementality for SKU-004.",
        impact: "Margin risk -2.3pts"
    },
    {
        title: "Retail Media Efficiency",
        detail: "Video placements outperform static placements by 1.8x ROI in week 2-4.",
        impact: "Spend reallocation opportunity +$75K"
    }
] as const;

const recommendations = [
    {
        title: "Rebalance Week 6 Promo Mix",
        owner: "Commercial Planning",
        eta: "2 days",
        rationale: "Move 20% of budget from low-yield TPR to media support on top-performing SKUs."
    },
    {
        title: "Protect Margin on Private Label",
        owner: "Category Team",
        eta: "This week",
        rationale: "Cap discount depth at 12% where cross-price elasticity is declining."
    },
    {
        title: "Expand Bundle Test",
        owner: "Growth Team",
        eta: "Next cycle",
        rationale: "Bundles in urban clusters improved conversion 14% without margin erosion."
    }
] as const;

const scenarioComparison = [
    {
        scenario: "Base Plan",
        revenue: "$1.24M",
        profit: "$312K",
        roi: "3.8x",
        confidence: "89%"
    },
    {
        scenario: "Aggressive Promotion",
        revenue: "$1.36M",
        profit: "$298K",
        roi: "3.1x",
        confidence: "83%"
    },
    {
        scenario: "Balanced Growth",
        revenue: "$1.31M",
        profit: "$334K",
        roi: "4.3x",
        confidence: "92%"
    }
] as const;

const Insights: React.FC = () => {
    return (
        <div className="container-fluid py-3">
            <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3 mb-3">
                <div>
                    <h2 className="h4 mb-1">Insights</h2>
                    <p className="text-muted mb-0">Explore strategic signals and recommended actions from your latest simulations.</p>
                </div>
                <div className="d-flex flex-wrap gap-2">
                    <button type="button" className="btn btn-outline-secondary">Export Brief</button>
                    <button type="button" className="btn btn-primary">Apply Top Recommendations</button>
                </div>
            </div>

            <div className="row g-3 mb-3">
                {summaryTiles.map(tile => (
                    <div className="col-12 col-sm-6 col-xl-3" key={tile.label}>
                        <Card className="h-100 shadow-sm">
                            <CardBody>
                                <div className="text-muted small mb-1">{tile.label}</div>
                                <div className="h2 mb-1">{tile.value}</div>
                                <span className="badge text-bg-success-subtle text-success">{tile.delta}</span>
                            </CardBody>
                        </Card>
                    </div>
                ))}
            </div>

            <div className="row g-3">
                <div className="col-12 col-xxl-8">
                    <StackLayout orientation="vertical" gap={16}>
                        <Card className="shadow-sm">
                            <CardHeader className="border-bottom">
                                <CardTitle className="h5 mb-0">Signal Monitor</CardTitle>
                            </CardHeader>
                            <CardBody>
                                <div className="row g-3">
                                    {trendSignals.map(signal => (
                                        <div className="col-12" key={signal.title}>
                                            <div className="border rounded-3 p-3 bg-light-subtle">
                                                <div className="d-flex flex-column flex-md-row justify-content-between gap-2 mb-2">
                                                    <h3 className="h6 mb-0">{signal.title}</h3>
                                                    <span className="badge rounded-pill text-bg-success-subtle text-success">{signal.impact}</span>
                                                </div>
                                                <p className="mb-0 text-muted">{signal.detail}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </CardBody>
                        </Card>

                        <Card className="shadow-sm">
                            <CardHeader className="border-bottom">
                                <CardTitle className="h5 mb-0">Scenario Comparison</CardTitle>
                            </CardHeader>
                            <CardBody className="p-0">
                                <div className="table-responsive">
                                    <table className="table align-middle table-hover mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th scope="col">Scenario</th>
                                                <th scope="col">Revenue</th>
                                                <th scope="col">Profit</th>
                                                <th scope="col">ROI</th>
                                                <th scope="col">Confidence</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {scenarioComparison.map(row => (
                                                <tr key={row.scenario}>
                                                    <td className="fw-semibold">{row.scenario}</td>
                                                    <td>{row.revenue}</td>
                                                    <td className="text-success fw-semibold">{row.profit}</td>
                                                    <td>{row.roi}</td>
                                                    <td>
                                                        <span className="badge rounded-pill text-bg-secondary">{row.confidence}</span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </CardBody>
                        </Card>
                    </StackLayout>
                </div>

                <div className="col-12 col-xxl-4">
                    <Card className="shadow-sm h-100">
                        <CardHeader className="border-bottom">
                            <CardTitle className="h5 mb-0">Recommended Next Actions</CardTitle>
                        </CardHeader>
                        <CardBody>
                            <StackLayout orientation="vertical" gap={12}>
                                {recommendations.map(item => (
                                    <div key={item.title} className="border rounded-3 p-3">
                                        <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                                            <h3 className="h6 mb-0">{item.title}</h3>
                                            <span className="badge text-bg-light border">{item.eta}</span>
                                        </div>
                                        <div className="small text-muted mb-2">
                                            Owner:
                                            {item.owner}
                                        </div>
                                        <p className="small mb-3">{item.rationale}</p>
                                        <button type="button" className="btn btn-sm btn-outline-primary">Send to Plan</button>
                                    </div>
                                ))}
                            </StackLayout>
                        </CardBody>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default Insights;
